import { Drawer } from "@mui/material";
import PropTypes from "prop-types";

import { Button, Divider, Icon, Tag } from "pepsico-ds";

import { PartnerContextProvider } from "../../../context/PartnerContext";

import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";

import _ from "lodash";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./filtersComponent.scss";

const FiltersComponent = ({
  filterConfig,
  filterClasses,
  onFilterChange,
  handleClearFilters,
  handleApplyFilters,
  viewAllFilters,
  setViewAllFilters,
  showAllFiltersOption,
  enableAllFiltersButton = true,
  debouncedFilterFormData,
  handleDebouncedFilterChange,
  filterData,
  allFiltersData,
  handleAllFiltersChange,
}) => {
  const { t } = useTranslation();
  const tagsToBeOmitted = ["page", "size"];

  const filterDataWithoutPagination = useMemo(
    () => _.omit(filterData, ["offset", "limit"]),
    [filterData]
  );

  // on change of filterData, call allFiltersChange
  useEffect(() => {
    const updatedFilterData = _.omit(filterData, ["offset", "limit"]);

    const allFiltersProperties =
      filterConfig.allFilters?.jsonSchema?.properties || {};
    const filteredData = _.pick(
      updatedFilterData,
      Object.keys(allFiltersProperties)
    );

    handleAllFiltersChange(filteredData);
  }, [
    filterData,
    handleAllFiltersChange,
    filterConfig.allFilters?.jsonSchema?.properties,
  ]);

  // Create a mapping of property keys to their labels from the schema
  const propertyLabels = useMemo(() => {
    const labelMap = {};

    if (filterConfig.allFilters?.jsonSchema?.properties) {
      Object.keys(filterConfig.allFilters.jsonSchema.properties).forEach((key) => {
        const property = filterConfig.allFilters.jsonSchema.properties[key];
        if (property.title) {
          labelMap[key] = property.title;
        }
      });
    }

    if (filterConfig.allFilters?.uiSchema?.elements) {
      filterConfig.allFilters.uiSchema.elements.forEach((element) => {
        if (element.type === "Control" && element.label && element.scope) {
          // Extract property name from scope e.g. "#/properties/engagementStatus" -> "engagementStatus"
          const propertyMatch = element.scope.match(/#\/properties\/(.+)$/);
          if (propertyMatch && propertyMatch[1]) {
            labelMap[propertyMatch[1]] = element.label;
          }
        }
      });
    }

    return labelMap;
  }, [filterConfig]);

  const displayFilterKeysAsTags = (data) => {
    // Get the specialized page info to exclude engagement type tag if needed
    const specializedPage = shouldExcludeEngagementTypeFilter();

    return (
      <div className="tags-container">
        {Object.keys(data)
          .filter((key) => {
            if (tagsToBeOmitted.includes(key)) return false;
            const value = data[key];

            // Skip showing engagementType tag on specialized pages when it matches the expected type
            if (
              specializedPage &&
              key === "engagementType" &&
              JSON.stringify(value) ===
                JSON.stringify([specializedPage.engagementType])
            ) {
              return false;
            }

            return value != null && String(value).trim() !== "";
          })
          .map((key) => {
            // Use the label from the schema, or fall back to formatting the key
            const label =
              propertyLabels[key] ||
              key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase());

            return (
              <Tag
                className="tag"
                key={key}
                text={label.trim()}
                isRemovable={true}
                isCopyable={false}
                onRemove={() => {
                  const updatedFilters = { ...data };
                  const updatedData = _.omit(updatedFilters, key);
                  handleAllFiltersChange(updatedData);
                }}
              />
            );
          })}
      </div>
    );
  };

  // Check if we're on a specialized engagement page that should exclude the engagementType filter from count
  const shouldExcludeEngagementTypeFilter = () => {
    const currentPath =
      typeof window !== "undefined" ? window.location?.pathname : "";
    const specializedPages = [
      { path: "receipt-scan", engagementType: "receipt-scan" },
      { path: "package-scan", engagementType: "package-scan" },
    ];

    // Only check includes if currentPath is defined
    return currentPath
      ? specializedPages.find((page) => currentPath.includes(page.path))
      : null;
  };

  const displayFilterCount = () => {
    if (!allFiltersData) return null;

    const specializedPage = shouldExcludeEngagementTypeFilter();

    const nonEmptyKeys = Object.keys(allFiltersData).filter((key) => {
      if (tagsToBeOmitted.includes(key)) return false;
      const value = allFiltersData[key];

      // Skip counting engagementType filter on specialized pages when it matches the expected type
      if (
        specializedPage &&
        key === "engagementType" &&
        JSON.stringify(value) === JSON.stringify([specializedPage.engagementType])
      ) {
        return false;
      }

      return value != null && String(value).trim() !== "";
    });

    return nonEmptyKeys.length > 0 ? (
      <Tag size="small" className="badge-count" text={nonEmptyKeys.length} />
    ) : null;
  };

  return (
    <div className="filter-container">
      {viewAllFilters && (
        <Drawer
          anchor="right"
          open={viewAllFilters}
          className="all-filter-drawer"
          data-testid="all-filters-drawer"
          onClose={() => setViewAllFilters(false)}
        >
          <div className="content-container">
            <div className="header-container">
              <h3 className="all-filter-header">All filters</h3>
              <Icon
                style={{ cursor: "pointer" }}
                alt="close"
                icon="close"
                size="medium"
                onClick={() => setViewAllFilters(false)}
              />
            </div>
            <div className="content">
              <div>{allFiltersData && displayFilterKeysAsTags(allFiltersData)}</div>

              <div className="filter-sub-header">
                Advanced filters
                {displayFilterCount()}
              </div>

              <JsonFormsWrapper
                jsonschema={filterConfig.allFilters?.jsonSchema}
                uischema={filterConfig.allFilters?.uiSchema}
                data={allFiltersData}
                onChange={handleAllFiltersChange}
              />
            </div>
            <div className="footer">
              <Button
                variant="primaryInverse"
                size="medium"
                onClick={handleClearFilters}
              >
                Clear all
              </Button>
              <Button
                style={{ color: "#ffffff" }}
                variant="primary"
                size="medium"
                onClick={handleApplyFilters}
              >
                Apply
              </Button>
            </div>
          </div>
        </Drawer>
      )}
      <div
        style={{ display: "flex", flex: 9 }}
        className={`inline-filter-container ${filterClasses}`}
      >
        {filterConfig.withDebounce?.jsonSchema &&
          filterConfig.withDebounce?.uiSchema && (
            <div className="debounced-filters" data-testid="json-forms-wrapper">
              <JsonFormsWrapper
                jsonschema={filterConfig.withDebounce.jsonSchema}
                uischema={filterConfig.withDebounce.uiSchema}
                data={debouncedFilterFormData}
                onChange={(data) => handleDebouncedFilterChange(data)}
              />
            </div>
          )}
        {filterConfig.withoutDebounce?.jsonSchema &&
          filterConfig.withoutDebounce?.uiSchema && (
            <div className="nonDebounced-filters" data-testid="json-forms-wrapper">
              <PartnerContextProvider>
                <JsonFormsWrapper
                  jsonschema={filterConfig.withoutDebounce.jsonSchema}
                  uischema={filterConfig.withoutDebounce.uiSchema}
                  data={filterDataWithoutPagination}
                  onChange={(data) => onFilterChange(data)}
                />
              </PartnerContextProvider>
            </div>
          )}

        {showAllFiltersOption && (
          <div
            className="all-clear-filters-btn"
            style={{
              display: "flex",
              alignItems: "end",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {enableAllFiltersButton && (
              <Button
                className={"filter-button"}
                variant="primaryInverse"
                size="small"
                onClick={() => setViewAllFilters(true)}
              >
                All filters {displayFilterCount()}
              </Button>
            )}
            <Divider
              orientation="vertical"
              style={{ height: "30px", backgroundColor: "rgba(0, 92, 188, 0.2)" }}
            />
            <Button
              className={"filter-button"}
              variant="primaryInverse"
              size="small"
              onClick={handleClearFilters}
            >
              {t("CustomerSupport:ClearAllButton")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

FiltersComponent.propTypes = {
  filterConfig: PropTypes.shape({
    allFilters: PropTypes.shape({
      jsonSchema: PropTypes.object,
      uiSchema: PropTypes.object,
    }),
    withDebounce: PropTypes.shape({
      jsonSchema: PropTypes.object,
      uiSchema: PropTypes.object,
    }),
    withoutDebounce: PropTypes.shape({
      jsonSchema: PropTypes.object,
      uiSchema: PropTypes.object,
    }),
  }).isRequired,
  filterClasses: PropTypes.string,
  onFilterChange: PropTypes.func.isRequired,
  handleClearFilters: PropTypes.func.isRequired,
  handleApplyFilters: PropTypes.func.isRequired,
  viewAllFilters: PropTypes.bool.isRequired,
  setViewAllFilters: PropTypes.func.isRequired,
  showAllFiltersOption: PropTypes.bool,
  enableAllFiltersButton: PropTypes.bool,
  debouncedFilterFormData: PropTypes.object,
  handleDebouncedFilterChange: PropTypes.func.isRequired,
  filterData: PropTypes.object,
  allFiltersData: PropTypes.object,
  handleAllFiltersChange: PropTypes.func.isRequired,
};

export default FiltersComponent;
